import { useCallback, useMemo } from "react";
import { keyBy } from "lodash";
import type { QueryHookOptions, TypedDocumentNode } from "@apollo/client";
import type { MultiValue, OnChangeValue, PropsValue } from "react-select";
import { DocumentVariablesType, useQuery } from "utils/apollo/hooks";
import { DocumentType } from "__generated__/gql";

export type IdValue<
  IsMulti extends boolean = false,
  TId = string,
> = IsMulti extends true ? TId[] : TId | null;

export type IdValueSelectProps<IsMulti extends boolean, TId = string> = {
  isMulti?: IsMulti;
  onChange?: (value: IdValue<IsMulti, TId>) => void;
  value?: IdValue<IsMulti, TId>;
};

type UseIdValueOptions<
  Option extends object,
  IsMulti extends boolean,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Query extends TypedDocumentNode<any, any>,
  TId = string,
> = IdValueSelectProps<IsMulti, TId> & {
  getOptionLabel?: (option: Option) => string | null | undefined;
  getOptionsFromData: (
    data: DocumentType<Query>,
  ) => Option[] | readonly Option[] | undefined | null;
  getVariables?: (ids: unknown[]) => DocumentVariablesType<Query>;
  labelKey?: keyof Option;
  query: Query;
  queryOptions?: Omit<
    QueryHookOptions<DocumentType<Query>, DocumentVariablesType<Query>>,
    "variables"
  >;
  valueKey?: keyof Option;
};

/**
 * A hook to be used with `react-select` components that enables the use of
 * simple scalar values instead of complete option objects as the `value` and
 * `onChange` props, e.g. for accepting just the `id` of an object as a `value`,
 * and calling the `onChange` handler with an `id`.
 *
 * To get the full option objects used in the display of values, a query is
 * made, which typically can be fulfilled by the existing cache, if correctly
 * configured.
 */
export default function useIdValue<
  Option extends object,
  IsMulti extends boolean,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Query extends TypedDocumentNode<any, any>,
  TId = string,
>({
  getOptionLabel: getOptionLabelProp,
  getOptionsFromData,
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  getVariables = (ids) => ({ ids } as DocumentVariablesType<Query>),
  isMulti,
  labelKey = "label" as keyof Option,
  onChange: idOnChange,
  query,
  queryOptions,
  value: idValue,
  valueKey = "id" as keyof Option,
}: UseIdValueOptions<Option, IsMulti, Query, TId>) {
  const getOptionValue = useCallback(
    (option: Option) => option[valueKey] as string,
    [valueKey],
  );

  const getOptionLabel = useCallback(
    (option: Option) =>
      ((getOptionLabelProp?.(option) ?? option[labelKey]) || " ") as string,
    [labelKey, getOptionLabelProp],
  );

  const idValueArray = useMemo(() => {
    if (!idValue) return [];
    if (Array.isArray(idValue)) return idValue as unknown[];
    return [idValue];
  }, [idValue]);

  const { data, loading: isLoading } = useQuery<
    DocumentType<Query>,
    DocumentVariablesType<Query>
  >(query, {
    ...queryOptions,
    skip: !idValueArray?.length || queryOptions?.skip,
    variables: getVariables(idValueArray),
  });

  const valueOptions = data ? getOptionsFromData(data) : null;

  const value = useMemo<PropsValue<Option>>(() => {
    if (!idValueArray?.length) return null;

    const optionsById = keyBy(valueOptions, valueKey);

    const arrayValue = idValueArray.map((id) => {
      const option = optionsById[id as keyof typeof optionsById];
      if (option) return option;
      return { [valueKey]: id, [labelKey]: "…" } as Option;
    });

    if (isMulti) return arrayValue;

    return arrayValue[0];
  }, [isMulti, idValueArray, valueOptions, valueKey, labelKey]);

  const onChange = useCallback(
    (newValue: OnChangeValue<Option, IsMulti>) => {
      idOnChange?.(
        (isMulti
          ? (newValue as MultiValue<Option>).map(getOptionValue)
          : newValue && getOptionValue(newValue as Option)) as IdValue<
          IsMulti,
          TId
        >,
      );
    },
    [isMulti, idOnChange, getOptionValue],
  );

  return {
    getOptionLabel,
    getOptionValue,
    isLoading,
    onChange,
    value,
  };
}
